import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    ChartPieSlice,
    DownloadSimple,
    Users,
    Handshake,
    Trophy,
    ClipboardText,
    IdentificationCard,
    Calendar,
    Timer,
    Funnel,
    TrendUp,
} from "@phosphor-icons/react";

const REPORTS = [
    { id: "joining",              label: "Joining",        icon: Trophy,             sub: "Hired / no-show" },
    { id: "offer",                label: "Offer",          icon: Handshake,          sub: "Accepted / rejected" },
    { id: "applicant-history",    label: "Applicants",     icon: ClipboardText,      sub: "All applications" },
    { id: "candidate-lineup",     label: "Candidate lineup", icon: IdentificationCard, sub: "Static + intake answers" },
    { id: "candidates",           label: "Candidates",     icon: IdentificationCard, sub: "KYC + registration" },
    { id: "recruiter-performance",label: "Recruiter perf", icon: ChartPieSlice,      sub: "Per-recruiter drill" },
    { id: "interviews",           label: "Interviews",     icon: Calendar,           sub: "Scheduled + past" },
    { id: "time-to-hire",         label: "Time-to-hire",   icon: Timer,              sub: "Funnel · median / p90" },
    { id: "source-of-hire",       label: "Source of hire", icon: Funnel,             sub: "Attribution split" },
    { id: "weekly-cohort",        label: "Cohort trend",   icon: TrendUp,            sub: "12-week hire velocity" },
];

// Column config per report — [{ key, label, formatter? }]
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};
const fmtMoney = (amt, cur) => {
    if (amt == null || amt === "") return "—";
    const symbols = { INR: "₹", USD: "$", GBP: "£", EUR: "€", AED: "د.إ", SGD: "S$" };
    const s = symbols[cur] || `${cur || ""} `;
    if (cur === "INR" && amt >= 100000) return `${s}${(amt / 100000).toFixed(2)} L`;
    return `${s}${Number(amt).toLocaleString()}`;
};
const fmtBool = (v) => (v ? "Yes" : "No");
const fmtList = (arr) => (Array.isArray(arr) && arr.length ? arr.slice(0, 3).join(", ") + (arr.length > 3 ? `+${arr.length - 3}` : "") : "—");
const fmtStage = (s) => (s ? (
    <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 ${
        s === "Hired" ? "bg-[#ECFDF5] text-[#059669]" :
        s === "Offer" ? "bg-[#FEF3C7] text-[#B45309]" :
        s === "Interview" ? "bg-[#DBEAFE] text-[#1D4ED8]" :
        s === "Screening" ? "bg-[#EDE9FE] text-[#6D28D9]" :
        s === "Rejected" ? "bg-[#FEE2E2] text-[#B91C1C]" :
        "bg-[#F5F5F5] text-[#525252]"
    }`}>{s}</span>
) : "—");
const fmtOutcome = (v, positive) => (
    v ? (
        <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 ${
            v === positive ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#FEE2E2] text-[#B91C1C]"
        }`}>{v.replace(/_/g, " ")}</span>
    ) : "—"
);

const COLUMNS = {
    "joining": [
        { key: "candidate_name",      label: "Candidate" },
        { key: "candidate_email",     label: "Email" },
        { key: "candidate_phone",     label: "Phone" },
        { key: "job_title",           label: "Job" },
        { key: "client_name",         label: "Client" },
        { key: "recruiter",           label: "Recruiter" },
        { key: "offer_amount",        label: "Offer",           render: (r) => fmtMoney(r.offer_amount, r.offer_currency) },
        { key: "expected_joining_date", label: "Expected joining", render: (r) => fmtDate(r.expected_joining_date) },
        { key: "actual_joining_date",   label: "Actual joining",   render: (r) => fmtDate(r.actual_joining_date) },
        { key: "hire_status",         label: "Hire status",     render: (r) => fmtOutcome(r.hire_status, "hired") },
        { key: "interview_dates",     label: "Interviews",      render: (r) => (r.interview_dates || []).map(fmtDate).join(", ") || "—" },
    ],
    "offer": [
        { key: "candidate_name",     label: "Candidate" },
        { key: "candidate_email",    label: "Email" },
        { key: "candidate_phone",    label: "Phone" },
        { key: "job_title",          label: "Job" },
        { key: "client_name",        label: "Client" },
        { key: "recruiter",          label: "Recruiter" },
        { key: "offer_amount",       label: "Offer",              render: (r) => fmtMoney(r.offer_amount, r.offer_currency) },
        { key: "expected_joining_date", label: "Expected joining", render: (r) => fmtDate(r.expected_joining_date) },
        { key: "offer_status",       label: "Offer status",        render: (r) => fmtOutcome(r.offer_status, "accepted") },
        { key: "recorded_at",        label: "Recorded",            render: (r) => fmtDateTime(r.recorded_at) },
    ],
    "applicant-history": [
        { key: "candidate_name", label: "Candidate" },
        { key: "candidate_email",label: "Email" },
        { key: "job_title",      label: "Job" },
        { key: "client_name",    label: "Client" },
        { key: "recruiter",      label: "Recruiter" },
        { key: "current_stage",  label: "Stage", render: (r) => fmtStage(r.current_stage) },
        { key: "tags",           label: "Tags",  render: (r) => fmtList(r.tags) },
        { key: "source",         label: "Source" },
        { key: "created_at",     label: "Applied", render: (r) => fmtDate(r.created_at) },
        { key: "updated_at",     label: "Updated", render: (r) => fmtDate(r.updated_at) },
    ],
    "candidates": [
        { key: "name",           label: "Name" },
        { key: "email",          label: "Email" },
        { key: "phone",          label: "Phone" },
        { key: "current_title",  label: "Title" },
        { key: "location",       label: "Location" },
        { key: "years_experience", label: "Yrs" },
        { key: "aadhar_verified", label: "Aadhaar", render: (r) => (
            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 ${r.aadhar_verified ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#F5F5F5] text-[#525252]"}`}>
                {fmtBool(r.aadhar_verified)}{r.aadhar_last4 ? ` · ****${r.aadhar_last4}` : ""}
            </span>
        ) },
        { key: "pan_verified",   label: "PAN",     render: (r) => (
            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 ${r.pan_verified ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#F5F5F5] text-[#525252]"}`}>
                {fmtBool(r.pan_verified)}{r.pan_last4 ? ` · ${r.pan_last4}` : ""}
            </span>
        ) },
        { key: "applications",   label: "Apps" },
        { key: "created_at",     label: "Registered", render: (r) => fmtDate(r.created_at) },
    ],
    "recruiter-performance": [
        { key: "name",  label: "Recruiter" },
        { key: "role",  label: "Role" },
        { key: "new_candidate_tag",        label: "New tagged" },
        { key: "new_candidate_lineup",     label: "New lineup" },
        { key: "previous_candidate_lineup",label: "Re-lineup" },
        { key: "offer",                    label: "Offer" },
        { key: "hired",                    label: "Hired" },
        { key: "registration_done",        label: "Reg. done" },
        {
            key: "registration_done_pct",
            label: "Reg. done %",
            render: (r) => {
                const p = r.registration_done_pct;
                if (p === null || p === undefined) {
                    return <span className="text-[#A3A3A3]">—</span>;
                }
                const cls =
                    p >= 60 ? "bg-[#ECFDF5] text-[#059669]" :
                    p >= 30 ? "bg-[#FEF3C7] text-[#B45309]" :
                              "bg-[#FEE2E2] text-[#B91C1C]";
                return (
                    <span
                        data-testid={`reg-pct-${r.id}`}
                        className={`inline-flex text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 ${cls}`}
                        title={`${r.registration_done || 0} of ${r.total_owned || 0} owned candidates registered`}
                    >
                        {p}%
                    </span>
                );
            },
        },
        { key: "testimonials_success",     label: "Testimonials" },
    ],
    "interviews": [
        { key: "candidate_name", label: "Candidate" },
        { key: "candidate_phone",label: "Phone" },
        { key: "job_title",      label: "Job" },
        { key: "job_location",   label: "Location" },
        { key: "client_name",    label: "Client" },
        { key: "recruiter",      label: "Scheduled by" },
        { key: "start",          label: "Interview start", render: (r) => fmtDateTime(r.start) },
        { key: "followup_outcome", label: "Outcome", render: (r) => (r.followup_outcome || "—") },
        { key: "current_stage",  label: "Current stage", render: (r) => fmtStage(r.current_stage) },
    ],
};

export default function Reports() {
    const [reportId, setReportId] = useState("joining");
    const [filters, setFilters] = useState({ client_id: "", job_id: "", start: "", end: "" });
    const [options, setOptions] = useState({ clients: [], jobs: [] });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch filter options once
    useEffect(() => {
        api.get("/reports/filter-options")
            .then((r) => setOptions(r.data || { clients: [], jobs: [] }))
            .catch((e) => logError("filter-options failed", e));
    }, []);

    const jobsForClient = useMemo(() => {
        if (!filters.client_id) return options.jobs;
        return options.jobs.filter((j) => j.client_id === filters.client_id);
    }, [filters.client_id, options.jobs]);

    // Client/job filters are ignored for "candidates" report per spec.
    const usesClientJobFilter = reportId !== "candidates";

    const loadData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (usesClientJobFilter && filters.client_id) params.set("client_id", filters.client_id);
        if (usesClientJobFilter && filters.job_id) params.set("job_id", filters.job_id);
        if (filters.start) params.set("start", filters.start);
        if (filters.end) params.set("end", filters.end);
        api.get(`/reports/${reportId}?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                logError("report load", e);
                toast.error("Couldn't load report.");
            })
            .finally(() => setLoading(false));
    }, [reportId, filters, usesClientJobFilter]);

    useEffect(() => { loadData(); }, [loadData]);

    const downloadCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (usesClientJobFilter && filters.client_id) params.set("client_id", filters.client_id);
            if (usesClientJobFilter && filters.job_id) params.set("job_id", filters.job_id);
            if (filters.start) params.set("start", filters.start);
            if (filters.end) params.set("end", filters.end);
            const r = await api.get(`/reports/${reportId}.csv?${params.toString()}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${reportId}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Downloaded — opens in Excel.");
        } catch (e) {
            toast.error("Export failed.");
        }
    };

    // V1.5.6.4 — XLSX export (currently wired only for candidate-lineup).
    const downloadXlsx = async () => {
        try {
            const params = new URLSearchParams();
            if (usesClientJobFilter && filters.client_id) params.set("client_id", filters.client_id);
            if (usesClientJobFilter && filters.job_id) params.set("job_id", filters.job_id);
            if (filters.start) params.set("start", filters.start);
            if (filters.end) params.set("end", filters.end);
            const r = await api.get(`/reports/${reportId}.xlsx?${params.toString()}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${reportId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Downloaded — Excel workbook.");
        } catch (e) {
            toast.error("XLSX export failed.");
        }
    };

    const cols = COLUMNS[reportId] || [];
    const rows = data?.items || [];
    const count = data?.count ?? rows.length;
    const isAggregate = reportId === "time-to-hire" || reportId === "source-of-hire" || reportId === "weekly-cohort";

    return (
        <div
            data-testid="reports-page"
            className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] w-full min-w-0 mx-auto"
            style={{ maxWidth: "min(1400px, 100vw)" }}
        >
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                        <ChartPieSlice size={12} weight="bold" /> Reporting
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Reports.
                    </h1>
                    <p className="text-sm text-[#525252] mt-2">
                        Slice your data by client, requirement or date range. Export any view to Excel.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="reports-export-csv"
                        onClick={downloadCsv}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90"
                    >
                        <DownloadSimple size={13} weight="bold" /> Export CSV
                    </button>
                    {reportId === "candidate-lineup" && (
                        <button
                            type="button"
                            data-testid="reports-export-xlsx"
                            onClick={downloadXlsx}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider border border-ink text-ink hover:bg-ink hover:text-white"
                        >
                            <DownloadSimple size={13} weight="bold" /> Export XLSX
                        </button>
                    )}
                </div>
            </div>

            {/* Report tabs */}
            <div
                data-testid="reports-tabs"
                className="flex flex-wrap gap-1 mb-6 border-b border-[#E5E5E5]"
            >
                {REPORTS.map(({ id, label, icon: Icon, sub }) => (
                    <button
                        key={id}
                        type="button"
                        data-testid={`reports-tab-${id}`}
                        onClick={() => setReportId(id)}
                        className={`px-4 py-3 border-b-2 -mb-px transition-colors text-left ${
                            reportId === id
                                ? "border-cobalt text-ink"
                                : "border-transparent text-[#525252] hover:text-ink"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Icon size={14} weight="bold" className={reportId === id ? "text-cobalt" : ""} />
                            <span className="text-[11px] font-black uppercase tracking-wider">{label}</span>
                        </div>
                        <p className="text-[9px] text-[#A3A3A3] font-medium mt-0.5">{sub}</p>
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div
                data-testid="reports-filters"
                className="flex flex-wrap items-end gap-3 mb-4"
            >
                {usesClientJobFilter && (
                    <>
                        <label className="flex flex-col">
                            <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">Client</span>
                            <select
                                data-testid="reports-filter-client"
                                value={filters.client_id}
                                onChange={(e) => setFilters((f) => ({ ...f, client_id: e.target.value, job_id: "" }))}
                                className="border border-[#E5E5E5] px-3 py-2 text-xs bg-white min-w-[180px]"
                            >
                                <option value="">All clients</option>
                                {options.clients.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col">
                            <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">Requirement</span>
                            <select
                                data-testid="reports-filter-job"
                                value={filters.job_id}
                                onChange={(e) => setFilters((f) => ({ ...f, job_id: e.target.value }))}
                                className="border border-[#E5E5E5] px-3 py-2 text-xs bg-white min-w-[220px]"
                            >
                                <option value="">All requirements</option>
                                {jobsForClient.map((j) => (
                                    <option key={j.id} value={j.id}>{j.title}</option>
                                ))}
                            </select>
                        </label>
                    </>
                )}
                <label className="flex flex-col">
                    <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">Start date</span>
                    <input
                        type="date"
                        data-testid="reports-filter-start"
                        value={filters.start}
                        onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
                        className="border border-[#E5E5E5] px-3 py-2 text-xs bg-white"
                    />
                </label>
                <label className="flex flex-col">
                    <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">End date</span>
                    <input
                        type="date"
                        data-testid="reports-filter-end"
                        value={filters.end}
                        onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
                        className="border border-[#E5E5E5] px-3 py-2 text-xs bg-white"
                    />
                </label>
                {(filters.client_id || filters.job_id || filters.start || filters.end) && (
                    <button
                        type="button"
                        data-testid="reports-clear-filters"
                        onClick={() => setFilters({ client_id: "", job_id: "", start: "", end: "" })}
                        className="text-[10px] font-black uppercase tracking-wider text-[#525252] hover:text-ink border border-[#E5E5E5] px-3 py-2"
                    >
                        Clear
                    </button>
                )}
                <div className="ml-auto text-[10px] font-black uppercase tracking-wider text-[#525252]">
                    {isAggregate ? (
                        reportId === "weekly-cohort" ? (
                            <>
                                <span data-testid="reports-row-count">{data?.totals?.applications ?? 0}</span> apps ·{" "}
                                {data?.totals?.hires ?? 0} hires · {data?.totals?.conversion_pct ?? 0}%
                            </>
                        ) : (
                            <><span data-testid="reports-row-count">{data?.total_hires ?? 0}</span> hires in scope</>
                        )
                    ) : (
                        <><span data-testid="reports-row-count">{count}</span> rows</>
                    )}
                </div>
            </div>

            {/* Aggregate views: Time-to-hire funnel */}
            {isAggregate && reportId === "time-to-hire" && !loading && data && (
                <TimeToHireCard data={data} />
            )}
            {isAggregate && reportId === "source-of-hire" && !loading && data && (
                <SourceOfHireCard data={data} />
            )}
            {isAggregate && reportId === "weekly-cohort" && !loading && data && (
                <WeeklyCohortCard data={data} />
            )}
            {isAggregate && loading && (
                <div className="bg-white border border-[#E5E5E5] p-10 text-center text-sm text-[#525252]">Loading…</div>
            )}

            {/* Table (for non-aggregate reports) */}
            {!isAggregate && (
            <div
                data-testid="reports-table-card"
                className="bg-white border border-[#E5E5E5] overflow-x-auto"
            >
                {loading && (
                    <div className="p-10 text-center text-sm text-[#525252]">Loading…</div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-10 text-center text-sm text-[#525252]">
                        No rows for this filter set. Try widening the date range.
                    </div>
                )}
                {/* V1.5.6.4 — Candidate Lineup renders a dynamic table because
                    intake fields (extra columns) vary per workspace. Everything
                    else uses the static COLUMNS map. */}
                {!loading && rows.length > 0 && reportId === "candidate-lineup" && (
                    <table className="w-full text-xs" data-testid="lineup-table">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr>
                                {(data?.headers || []).map((h) => (
                                    <th
                                        key={h}
                                        className="text-left p-2.5 text-[9px] font-black uppercase tracking-wider text-[#525252] whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr
                                    key={`${r.candidate_id}-${i}`}
                                    data-testid={`lineup-row-${i}`}
                                    className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]"
                                >
                                    <td className="p-2.5 whitespace-nowrap tabular-nums text-[#525252]">{r.date || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap font-mono text-ink">{r.candidate_id || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap text-ink">{r.candidate_name || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap text-ink">{r.job_title || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap text-[#525252]">{r.client_name || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap">{fmtStage(r.stage)}</td>
                                    <td className="p-2.5 whitespace-nowrap tabular-nums">{r.phone || "—"}</td>
                                    <td className="p-2.5 whitespace-nowrap">{r.email || "—"}</td>
                                    {(data?.template || []).map((f) => (
                                        <td
                                            key={f.key}
                                            className="p-2.5 whitespace-nowrap text-ink max-w-[260px] truncate"
                                            title={r.intake?.[f.key]}
                                        >
                                            {r.intake?.[f.key] || "—"}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {!loading && rows.length > 0 && reportId !== "candidate-lineup" && (
                    <table className="w-full text-xs">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr>
                                {cols.map((c) => (
                                    <th
                                        key={c.key}
                                        className="text-left p-2.5 text-[9px] font-black uppercase tracking-wider text-[#525252] whitespace-nowrap"
                                    >
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr
                                    key={r.candidate_id || r.user_id || r.id || `${reportId}-${i}`}
                                    data-testid={`reports-row-${i}`}
                                    className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]"
                                >
                                    {cols.map((c) => (
                                        <td
                                            key={c.key}
                                            className="p-2.5 text-ink whitespace-nowrap max-w-[260px] truncate"
                                            title={typeof r[c.key] === "string" ? r[c.key] : undefined}
                                        >
                                            {c.render ? c.render(r) : (r[c.key] ?? "—")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            )}
        </div>
    );
}

// ---------- Aggregate views ---------------------------------------------------

const daysFromMs = (ms) => (ms == null ? "—" : (ms / 86_400_000).toFixed(1));

const SEGMENT_META = [
    { key: "applied_to_screening", label: "Applied → Screening" },
    { key: "screening_to_interview", label: "Screening → Interview" },
    { key: "interview_to_offer", label: "Interview → Offer" },
    { key: "offer_to_hired", label: "Offer → Hired" },
];

function TimeToHireCard({ data }) {
    const applied = data?.segments?.applied_to_hired || {};
    return (
        <div data-testid="time-to-hire-card" className="bg-white border border-[#E5E5E5] p-5 sm:p-6 space-y-6">
            {/* Summary row: end-to-end */}
            <div className="border border-cobalt/25 bg-cobalt/5 px-4 py-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-cobalt">Applied → Hired · median</p>
                    <p className="font-heading text-4xl font-black tracking-tighter text-ink tabular-nums">
                        {daysFromMs(applied.median_ms)}<span className="text-lg text-[#525252] ml-1">days</span>
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Average</p>
                    <p className="font-heading text-2xl font-black tracking-tighter text-ink tabular-nums">
                        {daysFromMs(applied.avg_ms)}<span className="text-sm text-[#A3A3A3] ml-1">d</span>
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">p90 (slowest 10%)</p>
                    <p className="font-heading text-2xl font-black tracking-tighter text-ink tabular-nums">
                        {daysFromMs(applied.p90_ms)}<span className="text-sm text-[#A3A3A3] ml-1">d</span>
                    </p>
                </div>
                <div className="ml-auto text-right">
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Hires in scope</p>
                    <p className="font-heading text-2xl font-black tracking-tighter text-ink tabular-nums">
                        {data?.total_hires || 0}
                    </p>
                </div>
            </div>
            {/* Funnel rows */}
            <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-3">
                    Per-stage transitions
                </p>
                <div className="space-y-2">
                    {SEGMENT_META.map(({ key, label }) => {
                        const s = data.segments?.[key] || {};
                        const median = s.median_ms;
                        const width = median != null ? Math.min(100, (median / (applied.p90_ms || median || 1)) * 100) : 0;
                        return (
                            <div
                                key={key}
                                data-testid={`t2h-segment-${key}`}
                                className="grid grid-cols-[minmax(180px,1fr)_2fr_auto] gap-3 items-center"
                            >
                                <p className="text-xs font-bold text-ink truncate">{label}</p>
                                <div className="h-3 bg-[#F5F5F5] relative">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-cobalt"
                                        style={{ width: `${width}%` }}
                                    />
                                </div>
                                <p className="text-xs font-mono tabular-nums text-[#525252] min-w-[80px] text-right">
                                    <span className="font-bold text-ink">{daysFromMs(median)}d</span>
                                    <span className="text-[#A3A3A3]"> · n={s.count || 0}</span>
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
            {applied.count === 0 && (
                <p className="text-xs text-[#A3A3A3] italic">
                    No hires in this filter set — try widening the date range or removing client/job filters.
                </p>
            )}
        </div>
    );
}

function SourceOfHireCard({ data }) {
    const buckets = data?.buckets || [];
    const total = data?.total_hires || 0;
    // Palette for the source colors — matches SourceBadge tones elsewhere.
    const colorFor = (src) => ({
        self_apply: "bg-cobalt",
        public: "bg-[#059669]",
        manual: "bg-[#7E22CE]",
        csv: "bg-[#A3A3A3]",
        referral: "bg-[#F59E0B]",
    }[src] || "bg-ink");

    return (
        <div data-testid="source-of-hire-card" className="bg-white border border-[#E5E5E5] p-5 sm:p-6 space-y-6">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b border-[#F5F5F5] pb-4">
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Total hires</p>
                    <p className="font-heading text-4xl font-black tracking-tighter text-ink tabular-nums">
                        {total}
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Referral-attributed</p>
                    <p className="font-heading text-2xl font-black tracking-tighter text-ink tabular-nums">
                        {data?.referral_count || 0}
                        <span className="text-sm text-[#A3A3A3] ml-1">
                            ({total ? Math.round(100 * (data.referral_count || 0) / total) : 0}%)
                        </span>
                    </p>
                </div>
            </div>

            {/* Stacked bar */}
            <div className="w-full h-4 border border-[#E5E5E5] flex overflow-hidden" data-testid="soh-stacked-bar">
                {buckets.map((b) => (
                    <div
                        key={b.source}
                        title={`${b.label}: ${b.count} (${b.share_pct}%)`}
                        style={{ width: `${b.share_pct}%` }}
                        className={colorFor(b.source)}
                    />
                ))}
            </div>

            {/* Legend */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {buckets.map((b) => (
                    <div
                        key={b.source}
                        data-testid={`soh-bucket-${b.source}`}
                        className="border border-[#E5E5E5] px-3 py-2 flex items-center gap-3"
                    >
                        <span className={`w-3 h-3 shrink-0 ${colorFor(b.source)}`} />
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-ink">{b.label}</p>
                            <p className="text-[10px] text-[#525252]">
                                <span className="font-mono font-bold text-ink">{b.count}</span> hires · {b.share_pct}%
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {total === 0 && (
                <p className="text-xs text-[#A3A3A3] italic">
                    No hires attributed in this scope. Adjust filters or extend the date range.
                </p>
            )}
        </div>
    );
}

function WeeklyCohortCard({ data }) {
    const weeks = data?.weeks || [];
    const totals = data?.totals || { applications: 0, hires: 0, conversion_pct: 0 };
    const maxApps = Math.max(1, ...weeks.map((w) => w.applications || 0));
    // Sparkline path for the MA4 hires trend.
    const sparkPath = React.useMemo(() => {
        if (weeks.length < 2) return "";
        const maxY = Math.max(1, ...weeks.map((w) => w.hires_ma4 || 0));
        const w = 100 / (weeks.length - 1);
        return weeks
            .map((wk, i) => {
                const x = i * w;
                const y = 40 - ((wk.hires_ma4 || 0) / maxY) * 36;
                return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
            })
            .join(" ");
    }, [weeks]);

    return (
        <div data-testid="weekly-cohort-card" className="bg-white border border-[#E5E5E5] p-5 sm:p-6 space-y-6">
            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Total apps · 12w</p>
                    <p className="font-heading text-3xl font-black tracking-tighter text-ink tabular-nums">
                        {totals.applications}
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Hires · 12w</p>
                    <p className="font-heading text-3xl font-black tracking-tighter text-ink tabular-nums">
                        {totals.hires}
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Conversion</p>
                    <p className="font-heading text-3xl font-black tracking-tighter text-cobalt tabular-nums">
                        {totals.conversion_pct}<span className="text-base text-[#525252]">%</span>
                    </p>
                </div>
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#525252]">Weeks tracked</p>
                    <p className="font-heading text-3xl font-black tracking-tighter text-ink tabular-nums">
                        {weeks.length}
                    </p>
                </div>
            </div>

            {/* MA4 sparkline */}
            {sparkPath && (
                <div data-testid="weekly-cohort-sparkline" className="border-t border-[#F5F5F5] pt-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2">
                        4-week trailing hire velocity
                    </p>
                    <svg viewBox="0 0 100 44" preserveAspectRatio="none" className="w-full h-12">
                        <path d={sparkPath} stroke="#2563EB" strokeWidth="1.2" fill="none" vectorEffect="non-scaling-stroke" />
                        {/* end-of-line dot */}
                        {weeks.length > 0 && (() => {
                            const maxY = Math.max(1, ...weeks.map((w) => w.hires_ma4 || 0));
                            const last = weeks[weeks.length - 1];
                            const x = 100;
                            const y = 40 - ((last.hires_ma4 || 0) / maxY) * 36;
                            return <circle cx={x} cy={y} r="1.5" fill="#2563EB" />;
                        })()}
                    </svg>
                </div>
            )}

            {/* Weekly bars */}
            <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-3">
                    Applications vs hires by cohort week
                </p>
                <div className="space-y-1.5">
                    {weeks.map((w) => {
                        const appPct = (w.applications / maxApps) * 100;
                        const hirePct = w.applications > 0 ? (w.hires / w.applications) * appPct : 0;
                        return (
                            <div
                                key={w.week_start_iso}
                                data-testid={`weekly-cohort-week-${w.index}`}
                                className="grid grid-cols-[70px_minmax(0,1fr)_150px] gap-3 items-center text-xs"
                            >
                                <p className="text-[11px] font-mono text-[#525252]">{w.label}</p>
                                <div className="h-6 bg-[#F5F5F5] relative">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-cobalt/25"
                                        style={{ width: `${appPct}%` }}
                                        title={`${w.applications} applications`}
                                    />
                                    <div
                                        className="absolute inset-y-0 left-0 bg-cobalt"
                                        style={{ width: `${hirePct}%` }}
                                        title={`${w.hires} hires`}
                                    />
                                </div>
                                <p className="text-[11px] tabular-nums text-right text-[#525252]">
                                    <span className="font-bold text-ink">{w.applications}</span> apps ·{" "}
                                    <span className="font-bold text-cobalt">{w.hires}</span> hires
                                    {w.median_t2h_days != null && (
                                        <span className="text-[#A3A3A3]"> · {w.median_t2h_days}d</span>
                                    )}
                                </p>
                            </div>
                        );
                    })}
                </div>
                <div className="flex items-center gap-4 mt-4 text-[10px] font-black uppercase tracking-wider text-[#525252]">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-3 h-3 bg-cobalt/25" /> Applications
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-3 h-3 bg-cobalt" /> Hires
                    </span>
                </div>
            </div>

            {totals.applications === 0 && (
                <p className="text-xs text-[#A3A3A3] italic">
                    No candidates in the last {weeks.length} weeks. Add applicants or extend the range.
                </p>
            )}
        </div>
    );
}

